/**
 * Configuración de Convenios y Precios para Inscripción Modular.
 * Fácilmente extensible para agregar nuevos convenios en el futuro.
 */
export const CONVENIOS_MODULAR_CONFIG = {
  // Siglo XX
  9: {
    id: 9,
    nombre: 'Siglo XX',
    precios: {
      diplomado: 400,
      maestria: 500,
      especialidad: 400,
      curso: 400,
      default: 400,
    },
    descuentos: [
      { cantidad_minima: 6, porcentaje: 10, label: '10% de descuento por 6 o más módulos' },
      { cantidad_minima: 3, porcentaje: 5, label: '5% de descuento por 3 a 5 módulos' },
    ],
  },
  // UPI
  301: {
    id: 301,
    nombre: 'UPI',
    precios: {
      diplomado: 350,
      maestria: 450,
      especialidad: 350,
      curso: 350,
      default: 350,
    },
    descuentos: [
      { cantidad_minima: 6, porcentaje: 10, label: '10% de descuento por 6 o más módulos' },
      { cantidad_minima: 3, porcentaje: 5, label: '5% de descuento por 3 a 5 módulos' },
    ],
  },
};

/**
 * Determina el tipo de programa (diplomado, maestria, especialidad, curso).
 * @param {Object} programa 
 * @returns {string} 'diplomado' | 'maestria' | 'especialidad' | 'curso'
 */
export const obtenerTipoPrograma = (programa) => {
  if (!programa) return 'diplomado';

  const catNombre = (programa.postgrado?.categoria?.nombre || programa.tipo_programa || '').toLowerCase();
  if (catNombre.includes('maestria') || catNombre.includes('maestría')) return 'maestria';
  if (catNombre.includes('diplomado')) return 'diplomado';
  if (catNombre.includes('especialidad')) return 'especialidad';
  if (catNombre.includes('curso')) return 'curso';

  const nombre = (programa.nombre_compuesto || programa.nombre || '').toLowerCase();
  if (nombre.includes('maestria') || nombre.includes('maestría')) return 'maestria';
  if (nombre.includes('especialidad')) return 'especialidad';
  if (nombre.includes('curso')) return 'curso';
  if (nombre.includes('diplomado')) return 'diplomado';

  return 'diplomado';
};

/**
 * Obtiene la configuración de precios y descuentos para un convenio específico.
 * @param {number|string} convenioId 
 * @returns {Object|null}
 */
export const obtenerConfigConvenio = (convenioId) => {
  if (!convenioId) return null;
  return CONVENIOS_MODULAR_CONFIG[convenioId] || null;
};

/**
 * Calcula los detalles del plan modular (precios, subtotal, descuentos, total).
 * @param {Object} params
 * @param {Object|number} params.convenioId - ID o objeto del convenio
 * @param {Object} params.programa - Objeto del programa
 * @param {Array} params.modulosSeleccionados - Lista de módulos seleccionados
 * @param {Object} [params.planSeleccionado] - Plan de pagos seleccionado
 * @returns {Object} Detalle completo con información de costos y descuentos
 */
export const calcularResumenModular = ({ convenioId, programa, modulosSeleccionados = [], planSeleccionado = null, promo = null }) => {
  const idConvenio = typeof convenioId === 'object' ? convenioId?.id : convenioId;
  const config = obtenerConfigConvenio(idConvenio);
  const tipoProg = obtenerTipoPrograma(programa);
  const cantModulos = modulosSeleccionados.length;

  if (!config) {
    return {
      valido: false,
      mensaje: 'Seleccione un convenio válido para la inscripción modular.',
      tipoPrograma: tipoProg,
      precioBaseModulo: 0,
      cantModulos,
      subtotal: 0,
      porcentajeDescuento: 0,
      montoDescuento: 0,
      total: 0,
      descuentoAplicadoLabel: 'Sin convenio seleccionado',
      proximoDescuentoLabel: '',
    };
  }

  // Obtener el monto base del módulo desde el plan seleccionado (cobro.monto) convertiendolo a Number
  const cobroSeleccionado = planSeleccionado?.cobros?.[0];
  const montoRaw = cobroSeleccionado?.monto != null ? cobroSeleccionado.monto : planSeleccionado?.monto;
  const montoNum = montoRaw != null ? Number(montoRaw) : NaN;

  let precioBaseModulo = (!isNaN(montoNum) && montoNum > 0)
    ? montoNum
    : (config.precios[tipoProg] || config.precios.default || 400);

  // Aplicar descuento por promoción si existe (similar a generar_plan en utils.js)
  let montoDescuentoPorModulo = 0;
  let porcentajeDescuento = 0;
  let descuentoAplicadoLabel = 'Sin promoción aplicada';

  if (promo && (promo.id || promo.promocion_id)) {
    let reglaPromo = null;
    if (promo.detalle_promocion && promo.detalle_promocion.length > 0) {
      const conceptoId = cobroSeleccionado ? (cobroSeleccionado.concepto_id || cobroSeleccionado.concepto_pago_id) : null;
      reglaPromo = (conceptoId && promo.detalle_promocion.find((p) => p.concepto_pago_id == conceptoId || p.concepto_id == conceptoId)) || promo.detalle_promocion[0];
    }

    if (reglaPromo) {
      const valDesc = parseFloat(reglaPromo.valor_descuento || reglaPromo.descuento) || 0;
      const isPorcentaje = reglaPromo.is_porcentaje !== undefined ? reglaPromo.is_porcentaje : (reglaPromo.tipo_descuento === 'PORCENTAJE' || reglaPromo.es_porcentaje);
      if (isPorcentaje) {
        porcentajeDescuento = valDesc;
        montoDescuentoPorModulo = precioBaseModulo * (valDesc / 100);
        descuentoAplicadoLabel = `Promoción (${promo.nombre || 'Aplicada'}): ${valDesc}% de descuento por módulo`;
      } else {
        montoDescuentoPorModulo = valDesc;
        if (precioBaseModulo > 0) {
          porcentajeDescuento = Number(((montoDescuentoPorModulo / precioBaseModulo) * 100).toFixed(2));
        }
        descuentoAplicadoLabel = `Promoción (${promo.nombre || 'Aplicada'}): Bs. ${valDesc} de descuento por módulo`;
      }
    } else if (promo.valor_descuento != null || promo.descuento != null || promo.porcentaje != null) {
      const valDesc = parseFloat(promo.valor_descuento || promo.descuento || promo.porcentaje) || 0;
      const isPorcentaje = promo.is_porcentaje !== undefined ? promo.is_porcentaje : true;
      if (isPorcentaje) {
        porcentajeDescuento = valDesc;
        montoDescuentoPorModulo = precioBaseModulo * (valDesc / 100);
        descuentoAplicadoLabel = `Promoción (${promo.nombre || 'Aplicada'}): ${valDesc}% de descuento por módulo`;
      } else {
        montoDescuentoPorModulo = valDesc;
        if (precioBaseModulo > 0) {
          porcentajeDescuento = Number(((montoDescuentoPorModulo / precioBaseModulo) * 100).toFixed(2));
        }
        descuentoAplicadoLabel = `Promoción (${promo.nombre || 'Aplicada'}): Bs. ${valDesc} de descuento por módulo`;
      }
    }
  }

  const precioFinalModulo = Number(Math.max(0, precioBaseModulo - montoDescuentoPorModulo).toFixed(2));
  const subtotal = Number((cantModulos * precioBaseModulo).toFixed(2));
  const montoDescuento = Number((cantModulos * montoDescuentoPorModulo).toFixed(2));
  const total = Number((cantModulos * precioFinalModulo).toFixed(2));

  return {
    valido: true,
    convenioNombre: config.nombre,
    tipoPrograma: tipoProg,
    tipoProgramaLabel: tipoProg.charAt(0).toUpperCase() + tipoProg.slice(1),
    precioBaseModulo,
    precioFinalModulo,
    cantModulos,
    subtotal,
    porcentajeDescuento,
    montoDescuento,
    total,
    descuentoAplicadoLabel,
    proximoDescuentoLabel: '',
  };
};

/**
 * Genera el plan de pagos (cronograma) para inscripción modular.
 * @param {Object} params
 * @param {Object|number} params.convenioId
 * @param {Object} params.programa
 * @param {Array} params.modulosSeleccionados
 * @param {Object} [params.planSeleccionado]
 * @param {Object} [params.promo]
 * @returns {Object} { plan: Array, resumen: Object }
 */
export const generarPlanModular = ({ convenioId, programa, modulosSeleccionados = [], planSeleccionado = null, promo = null }) => {
  const resumen = calcularResumenModular({ convenioId, programa, modulosSeleccionados, planSeleccionado, promo });

  if (!resumen.valido || modulosSeleccionados.length === 0) {
    return {
      plan: [],
      resumen,
    };
  }

  const cobroSeleccionado = planSeleccionado?.cobros?.[0];

  const cobroProgramaId =
    cobroSeleccionado?.cobro_programa_id ||
    planSeleccionado?.cobro_programa_id ||
    planSeleccionado?.plan_cobro_programa_id ||
    (programa?.planes?.[0]?.cobros?.[0]?.cobro_programa_id) ||
    null;

  const nombreConcepto =
    cobroSeleccionado?.nombre ||
    cobroSeleccionado?.concepto_nombre ||
    cobroSeleccionado?.concepto?.nombre ||
    planSeleccionado?.nombre ||
    programa?.planes?.[0]?.cobros?.[0]?.nombre ||
    'COLEGIATURA';

  // Ordenar módulos seleccionados por número
  const modulosOrdenados = [...modulosSeleccionados].sort((a, b) => Number(a.numero || 0) - Number(b.numero || 0));
  const cant = modulosOrdenados.length;
  
  // Distribuir el monto total equitativamente entre las cuotas/módulos
  const montoTotal = resumen.total;
  const cuotaBase = Math.floor(montoTotal / cant);
  const residuo = Math.round(montoTotal - cuotaBase * cant);

  const hoy = new Date();

  const formatFecha = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // Si se aplica una promoción en el plan modular, el cronograma debe tener un único ítem con el monto total
  const tienePromocion = Boolean(promo && (promo.id || promo.promocion_id));

  if (tienePromocion) {
    const plan = [
      {
        cobro_programa_id: cobroProgramaId,
        nro_cuota: 1,
        num_cuota_grupo: 1,
        xnombre: nombreConcepto,
        modulo_nombre: null,
        xmodulo_nombre: null,
        nombre_modulo: '',
        monto: montoTotal,
        fecha_pago: formatFecha(hoy),
        programa_modulo_id: null,
        idmodulo: null,
      },
    ];

    return {
      plan,
      resumen,
    };
  }

  const plan = modulosOrdenados.map((item, index) => {
    // La primera cuota recibe el residuo del redondeo para llevar el monto mayor si aplica
    const montoCuota = index === 0 ? cuotaBase + residuo : cuotaBase;

    // Determinar nombre del módulo
    const numModulo = item.numero || index + 1;
    const nombreModulo = item.modulo?.nombre || item.nombre || `Módulo ${numModulo}`;
    const moduloNombreCompleto = `- ${nombreModulo}`;

    // Determinar fecha de pago (primer ítem hoy, siguientes el 15 de cada mes)
    let fechaPagoStr = null;
    if (index === 0) {
      fechaPagoStr = formatFecha(hoy);
    } else {
      const fechaCuota = new Date(hoy.getFullYear(), hoy.getMonth() + index, 15);
      fechaPagoStr = formatFecha(fechaCuota);
    }

    return {
      cobro_programa_id: cobroProgramaId,
      nro_cuota: index + 1,
      num_cuota_grupo: index + 1,
      xnombre: nombreConcepto,
      modulo_nombre: moduloNombreCompleto,
      xmodulo_nombre: moduloNombreCompleto,
      nombre_modulo: nombreModulo,
      monto: montoCuota,
      fecha_pago: fechaPagoStr,
      programa_modulo_id: item.id,
      idmodulo: item.idmodulo,
    };
  });

  return {
    plan,
    resumen,
  };
};
